/*
 * @Author: Zhouqi
 * @Date: 2025-11-03 19:40:27
 * @LastEditors: Ryan
 * @LastEditTime: 2026-01-16
 */
import dynamic from 'next/dynamic';
import React from 'react';

import VideoGenerationSkeleton from '@/3.0/video-generation/_components/video-generation-skeleton';
import { Introduction } from '@/components/container';
import DelayedRender from '@/components/DelayRender';
import Footer from '@/components/Footer';
import Layout from '@/components/layout';
import SEOHead from '@/landing/components/SEOHead';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

// 预加载资源 - 默认显示第3个视频(index=2)的 poster
const PRELOAD_POSTER =
  'https://assets.falcocut.ai/ai-video-generator/hero-tutorial-video-poster.webp';

// 动态加载完整的 VideoGeneration
const VideoGeneration = dynamic(() => import('../../3.0/video-generation/entry'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <VideoGenerationSkeleton />
    </DelayedRender>
  ),
});

const SEOSections = dynamic(() => import('../../3.0/video-generation/seo'), {
  ssr: true,
});

// SEO 常量
const PAGE_TITLE = 'AI Video Generator - Create Videos with AI | FalcoCut';
const META_DESCRIPTION =
  'Create stunning AI-powered videos in minutes. Transform text to video, generate talking avatars, and produce professional content with FalcoCut AI Video Generator.';
const CURRENT_URL = 'https://falcocut.ai/ai-video-generator';

// 结构化数据
const structuredData = createSoftwareAppSchema({
  name: 'FalcoCut AI Video Generator',
  description: META_DESCRIPTION,
  url: CURRENT_URL,
  ratingValue: '4.9',
  ratingCount: '4520',
  featureList: [
    'Text to video generation',
    'AI avatar creation',
    'Voice cloning and dubbing',
    'Automatic lip sync',
    'Multi-language support',
    'Professional video templates',
  ],
});

const VideoGenerationPage: React.FC & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  getExtraContent?: () => React.ReactNode;
} = () => {
  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={META_DESCRIPTION}
        url={CURRENT_URL}
        imageAlt="FalcoCut AI Video Generator"
        publishedTime="2025-11-03"
        modifiedTime="2026-01-16"
        structuredData={structuredData}
        preloadImages={[PRELOAD_POSTER]}
      />

      {/* 客户端动态加载完整组件（保留骨架屏） */}
      <VideoGeneration />
    </>
  );
};

// 定义 getLayout 为直接返回页面本身，不使用 Layout
VideoGenerationPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

// 定义 SEO 部分和 Footer 作为额外内容，在 Layout 外渲染
VideoGenerationPage.getExtraContent = () => {
  return (
    <Introduction>
      <SEOSections />
      <Footer />
    </Introduction>
  );
};

export const getStaticProps = async () => {
  return {
    props: {},
  };
};

export default VideoGenerationPage;
